import { useState, useEffect } from "react";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import AddButton from "../../../../../components/AddButton";
import EditButton from "../../../../../components/EditButton";
import ConfirmBoxDelete from "../../../../../components/ConfirmBoxDelete";
import MaterialTableTS from "../../../../../components/tables/MaterialTableTS";
import { permissionsSelector } from "../../../../Login/selectors/permissionSelector";
import { PERMISSIONS } from "../../../../PermissionsConstants";
import TableFilters from "../../TableFilters";
import {
  addStateLineOfBusiness,
  editStateLineOfBusiness,
  rowForEditLineOfBusiness,
  filterText,
  selectedRowsStateLineOfBusiness,
  lineOfBusinessFetching,
} from "../atoms/atoms";
import useGetLineOfBusiness from "../hooks/useGetLineOfBusiness";
import useDeactivateLineOfBusiness from "../hooks/useDeleteLineOfBusiness";
import { LineOfBusinessForm } from "../form/LineOfBusinessForm";
import { useHistory, useLocation } from "react-router-dom";
import { notificationFilterById } from "../../../../../actions/Utilities/NotificationFilter";
import { lineOfBusinessInfo } from "../types";
import Timer from "../../Timer";
export const LineOfBusinessTable = () => {
  const BulkDeleteLineOfBusiness = useDeactivateLineOfBusiness();
  const [lineOfBusinessList, setLineOfBusinessList] = useState<
    lineOfBusinessInfo[]
  >([]);

  //Hooks Call
  const {
    data: lineOfBusinessOriginal,
    isSuccess: lineOfBusinessSuccess,
    isFetching: fetching,
    refetch: refetch,
  } = useGetLineOfBusiness();

  useEffect(() => {
    if (lineOfBusinessSuccess && lineOfBusinessOriginal) {
      setLineOfBusinessList(
        lineOfBusinessOriginal.map((data) => ({ ...data }))
      );
    }
    setIsFetching(false);
  }, [lineOfBusinessSuccess, lineOfBusinessOriginal]);

  const [addState, setAddState] = useRecoilState(addStateLineOfBusiness);
  const [editState, setEditState] = useRecoilState(editStateLineOfBusiness);
  const [selectedRows, setSelectedRows] = useRecoilState(
    selectedRowsStateLineOfBusiness
  );
  const [isFetching, setIsFetching] = useRecoilState(lineOfBusinessFetching);
  const setRowToEdit = useSetRecoilState(rowForEditLineOfBusiness);
  const [filterTextState, setFilterTextState] = useRecoilState(filterText);
  const [disabledButton, setDisabledButton] = useState(false);
  const [openModalDelete, setOpenModalDelete] = useState(false);
  const [rowsCount, setRowsCount] = useState(0);
  const [lineOfBusinessFilteredData, setLineOfBusinessFilteredData] = useState<
    lineOfBusinessInfo[]
  >([]);

  // permissions

  const userPermissions = useRecoilValue(permissionsSelector);
  const addPermission =
    PERMISSIONS.permission_utilities_configureagreement_lineofbusiness_add;
  const deletePermission =
    PERMISSIONS.permission_utilities_configureagreement_lineofbusiness_delete;
  const editPermission =
    PERMISSIONS.permission_utilities_configureagreement_lineofbusiness_edit;

  //For filtered data update
  const [lineOfBusinessId, setLineOfBusinessId] = useState("");
  useEffect(() => {
    if (lineOfBusinessId?.includes(".")) {
      let ids = lineOfBusinessId.split(".");
      const arr = ids.map((id) => Number(id));
      const filteredData = lineOfBusinessList?.filter((x) =>
        arr.includes(x.lineOfBusinessId)
      );
      setLineOfBusinessFilteredData(filteredData);
    } else {
      const filteredData = lineOfBusinessList?.filter(
        (x) => x.lineOfBusinessId === Number(lineOfBusinessId)
      );
      setLineOfBusinessFilteredData(filteredData);
    }
  }, [lineOfBusinessList, lineOfBusinessId]);

  let location = useLocation();
  const history = useHistory();
  let recordIdFromRecent;

  if (location.state == null) {
    //Do nothing
  } else {
    recordIdFromRecent = location.state;
  }

  useEffect(() => {
    if (recordIdFromRecent) {
      try {
        notificationFilterById(
          recordIdFromRecent.recordId,
          recordIdFromRecent.moduleName,
          recordIdFromRecent.subModuleName
        ).then((result) => {
          setLineOfBusinessFilteredData(result);
          setLineOfBusinessId(recordIdFromRecent.recordId);
        });
      } catch (error) {}
    } else {
      localStorage.removeItem("NotifFilter");
      localStorage.removeItem("ModuleFilter");
    }
  }, [history, recordIdFromRecent]);

  const filteredItems = lineOfBusinessList
    ? lineOfBusinessList?.filter((item) =>
        item.lineOfBusinessName
          ?.toLowerCase()
          .includes(filterTextState?.trim().toLowerCase())
      )
    : [];

  const handleEdit = (rowDetails) => {
    setRowToEdit({ ...rowDetails });
    setEditState(true);
  };

  const handleSelectedRowsChange = (rows) => {
    const forDelete = rows
      .filter((x) => x.isActive)
      .map((r) => {
        const { tableData, ...rows } = r;
        return rows;
      });
    setSelectedRows(forDelete);
    setRowsCount(forDelete.length);
  };

  const handleDelete = async () => {
    setIsFetching(true);
    const ids = selectedRows && selectedRows.map((row) => row.lineOfBusinessId);
    setSelectedRows([]);
    setOpenModalDelete(false);
    BulkDeleteLineOfBusiness(ids);
    await Timer();
    refetch();
    setIsFetching(false);
  };

  const deleteMessage = (
    <div className="text-gray-500 font-normal">{`Are you sure you want to deactivate ${rowsCount} ${
      rowsCount > 1 ? "Line of Businesses" : "Line of Business"
    }?`}</div>
  );

  const handleAdd = () => {
    setAddState(true);
  };

  const sortByString = (a: string, b: string) => {
    if (a && b) {
      return a.trim().localeCompare(b.trim());
    } else if (a) {
      return 0;
    } else {
      return -1;
    }
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString();
  };

  const matColumns = [
    {
      title: "Line of Business",
      field: "lineOfBusinessName",
      customSort: (a, b) =>
        sortByString(a.lineOfBusinessName, b.lineOfBusinessName),
      render: (rowData) => rowData.lineOfBusinessName,
    },
    {
      title: "Business Type Name",
      field: "businessTypeName",
      customSort: (a, b) =>
        sortByString(a.businessTypeName, b.businessTypeName),
      render: (rowData) => rowData.businessTypeName,
    },
    {
      title: "Created Date",
      field: "createdOn",
      type: "date" as "date",
      dateSetting: { locale: "en-US", format: "MM/dd/yyyy" },
      render: (rowData) =>
        rowData?.createdOn ? (
          <div className="">
            <div>{formatDate(rowData.createdOn)}</div>
          </div>
        ) : (
          ""
        ),
    },
    {
      title: "Created By",
      field: "createdBy",
      render: (rowData) => rowData.createdBy,
    },
    {
      title: "Modified Date",
      field: "modifiedOn",
      type: "date" as "date",
      dateSetting: { locale: "en-US", format: "MM/dd/yyyy" },
      render: (rowData) =>
        rowData?.modifiedOn ? (
          <div className="">
            <div>{formatDate(rowData.modifiedOn)}</div>
          </div>
        ) : (
          ""
        ),
    },
    {
      title: "Modified By",
      field: "modifiedBy",
      customSort: (a, b) => sortByString(a.modifiedBy, b.modifiedBy),
    },
    {
      field: "isActive",
      title: "Status",
      align: "center" as "center",
      render: (rowData) => (rowData.isActive ? "Active" : "Inactive"),
    },
    {
      title: "Action/s",
      align: "center" as "center",
      hidden: !userPermissions.includes(editPermission),
      render: (rowData) => {
        return (
          <div className="flex justify-items-center mr-5">
            <EditButton
              className={""}
              disabled={
                editState ||
                (selectedRows !== undefined && selectedRows.length > 0)
              }
              onClick={() => {
                handleEdit(rowData);
              }}
            />
          </div>
        );
      },
    },
  ];

  return (
    <>
      <div className="bg-white p-4">
        {addState || editState ? (
          <div>
            <LineOfBusinessForm refetch={refetch} />
          </div>
        ) : (
          <div className="">
            <div className="flex w-full">
              <div className="bg-white w-full">
                <div className="flex flex-row justify-between items-center">
                  <div className="">
                    <TableFilters
                      moduleDetails={"LineOfBusiness"}
                      textInputName={"LineOfBusiness"}
                      columnDetails={"Line of Business"}
                      filterText={setFilterTextState}
                      disabledButton={
                        editState ||
                        (selectedRows !== undefined && selectedRows.length > 0)
                      }
                      disabledTextBox={
                        editState ||
                        (selectedRows !== undefined && selectedRows.length > 0)
                      }
                    />
                  </div>
                  <div>
                    {userPermissions.includes(deletePermission) ? (
                      <div className="text-secondary italic text-xs3 my-2">
                        <>
                          <AddButton
                            buttonText="New Line of Business"
                            className={
                              selectedRows.length > 0 || editState
                                ? "opacity-50 float-right"
                                : "opacity-100 float-right"
                            }
                            onClick={() => handleAdd()}
                            disabled={disabledButton || selectedRows.length > 0}
                          />
                        </>
                        {/* <p className="font-bold italic inline-block mr-1">
                          Note:
                        </p>
                        <span>Tick the box to deactivate some data.</span> */}
                      </div>
                    ) : (
                      <div className="mb-4"></div>
                    )}
                  </div>
                </div>
                <div>
                  {userPermissions.includes(deletePermission) ? (
                    <div className="text-secondary italic text-xs3 my-2">
                      <p className="font-bold italic inline-block mr-1">
                        Note:
                      </p>
                      <span>Tick the box to deactivate some data.</span>
                    </div>
                  ) : (
                    <div className="mb-4"></div>
                  )}
                </div>

                <div>
                  <MaterialTableTS
                    data={
                      isFetching
                        ? []
                        : lineOfBusinessFilteredData?.length > 0
                        ? lineOfBusinessFilteredData
                        : filterTextState === ""
                        ? lineOfBusinessList
                        : filteredItems
                    }
                    showTitle={true}
                    columns={matColumns}
                    onSelectionChange={handleSelectedRowsChange}
                    selectedRows={selectedRows}
                    selection={
                      !editState &&
                      !addState &&
                      userPermissions.includes(deletePermission)
                    }
                    isLoading={fetching}
                    setConfirmModalOpen={setOpenModalDelete}
                  />
                </div>
              </div>
            </div>
            <ConfirmBoxDelete
              ModalTitle="Deactivate Line of Business"
              qConfirmation={deleteMessage}
              isOpen={openModalDelete}
              onClose={() => {
                setOpenModalDelete(false);
              }}
              onClickFunction={handleDelete}
              cancelButtonRef={undefined}
            />
          </div>
        )}
      </div>
    </>
  );
};
